import React, { useState, useEffect } from 'react';
import { AuthService, UsersService, OpenAPI } from '@/lib/api-generated';
import { AuthContext, User } from '@/hooks/use-auth-context';
import { logger } from '@/lib/logger';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for token in localStorage on mount
    const storedToken = localStorage.getItem('access_token');
    if (storedToken) {
      OpenAPI.TOKEN = storedToken;
    }
    
    // Set TOKEN to always use current localStorage value
    OpenAPI.TOKEN = async () => {
      const token = localStorage.getItem('access_token');
      return token || undefined;
    };
    
    checkAuth();

    const handleUnauthorized = () => {
      setUser(null);
      OpenAPI.TOKEN = undefined;
      localStorage.removeItem('access_token');
      
      // Optional: redirect to login if not already there
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/signup')) {
        window.location.href = '/login';
      }
    };

    window.addEventListener('unauthorized', handleUnauthorized);
    return () => window.removeEventListener('unauthorized', handleUnauthorized);
  }, []);

  const checkAuth = async () => {
    try {
      // If we have a token (restored from localStorage or set in login), use it.
      // If not, we still try (maybe cookies work in some environments, or for guest access if supported)
      // but primarily we rely on the token now for cross-domain auth.
      
      // Using generated client
      const userData = await UsersService.getUserMeApiV1UsersMeGet();
      setUser(userData as unknown as User);
    } catch (error) {
      // User is not authenticated
      logger.warn("AuthContext: checkAuth failed, clearing session", { error });
      setUser(null);
      OpenAPI.TOKEN = undefined;
      localStorage.removeItem('access_token');
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      logger.debug("AuthContext: Starting login for", { email });
      
      const tokenData = await AuthService.loginApiV1AuthLoginPost({ 
        username: email, 
        password: password 
      });
      
      logger.debug("AuthContext: Login API successful");
      
      // Store token for persistence
      localStorage.setItem('access_token', tokenData.access_token);
      
      // Update generated client config
      OpenAPI.TOKEN = tokenData.access_token;

      logger.debug("AuthContext: Fetching current user...");
      const userData = await UsersService.getUserMeApiV1UsersMeGet();
      logger.debug("AuthContext: Current user fetched successfully", { email: userData.email });
      
      setUser(userData as unknown as User);
    } catch (error) {
      logger.error("AuthContext: Login failed", error);
      throw error;
    }
  };

  const register = async (email: string, password: string, fullName?: string) => {
    const response = await AuthService.registerApiV1AuthRegisterPost({
      email,
      password,
      full_name: fullName
    });

    // Check if response contains tokens (backend update) to bypass unverified login restriction
    const responseWithToken = response as unknown as { access_token?: string };
    if (responseWithToken.access_token) {
      logger.debug("AuthContext: Registration returned token, auto-logging in...");
      localStorage.setItem('access_token', responseWithToken.access_token);
      OpenAPI.TOKEN = responseWithToken.access_token;
      setUser(response as unknown as User);
    } else {
      logger.debug("AuthContext: Registration didn't return token, attempting manual login...");
      await login(email, password);
    }
  };

  const logout = () => {
    OpenAPI.TOKEN = undefined;
    localStorage.removeItem('access_token');
    setUser(null);
  };

  const resendVerification = async (email: string): Promise<{ message: string }> => {
    // The generated client returns 'any', so we cast or assume the structure
    return await AuthService.resendVerificationApiV1AuthResendVerificationPost({ email }) as { message: string };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        resendVerification,
        logout,
        refreshUser: checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

